import { canonicalizeLocale } from './canonicalize';
import { createResolver } from './resolve';

/**
 * The fixed subset of i18next `InitOptions` that every Joy service should
 * share. All fields carry literal types so that:
 *
 * - Runtime: `buildI18nextConfig` always emits these exact values.
 * - Type: any consumer attempting to override a locked field (e.g.
 *   `load: 'currentOnly'`) collides with the literal in the return
 *   type intersection, which collapses that field to `never` and surfaces
 *   the mistake at the spread site.
 *
 * Why the joy-web PR #15872 monkey-patch (`applyClosestSiblingResolver`) is
 * gone: with `detection.convertDetectedLanguage` baked in below to run
 * `resolveLocale` on every input before i18next sees it, i18next only ever
 * receives an exact supported locale code. The native resolve chain for
 * `de-DE` is already `['de-DE', 'en-US']` — no injection needed. This
 * invariant depends on every code path that hands a locale to i18next
 * going through the detection hook, `cleanCode`, or having already been
 * canonicalized (e.g. an SSR-seeded value that the server already resolved).
 *
 * `detection` is an OPAQUE LOCKED BLOCK. Consumers MUST NOT do
 * `{ ...buildI18nextConfig(...), detection: { order: [...] } }` — that
 * spread replaces the entire object and drops `convertDetectedLanguage`.
 * If you need to extend detection, spread the base's detection first:
 *
 *   const base = buildI18nextConfig({...});
 *   const config = { ...base, detection: { ...base.detection, order: [...] } };
 */
export interface I18nextBaseConfig {
  /**
   * The service's supported locale set. Sourced from the `supportedLocales`
   * opt. Consumers can (and should) supply their own array here — this field
   * is not locked to a specific value, only to the shape.
   */
  supportedLngs: string[];

  /**
   * The service's fallback locale. Sourced from the `fallbackLocale` opt.
   * Not locked to a specific value.
   */
  fallbackLng: string;

  /**
   * Locked to `'all'`. Ensures i18next loads the ENTIRE resolve chain
   * (e.g. `['de-DE', 'en-US']`) rather than just the active language. The
   * alternative `'languageOnly'` was the root cause of the joy-web
   * en-GB/en-AU silent-fallback bug — see PR #15872. `'currentOnly'` breaks
   * fallback entirely. There is no legitimate service-specific reason to
   * pick anything other than `'all'`, so the lock prevents future
   * copy-paste drift.
   */
  load: 'all';

  /**
   * Locked to `false`. We resolve fuzzy input (`de-AT` → `de-DE`, bare
   * `de` → `de-DE`, case/underscore normalization, unsupported fallback)
   * OURSELVES via `resolveLocale` in `detection.convertDetectedLanguage`
   * below, before i18next ever sees the code. i18next's native non-explicit
   * matching (which is what setting this to `true` enables) would either
   * double-resolve or collide with our resolver — worse, it strips region
   * before the `supportedLngs` lookup, which rejects every explicit BCP 47
   * code (`de-DE → de → not in supportedLngs`). That regression was the
   * exact bug PR #15872 fixed. Lock stays.
   */
  nonExplicitSupportedLngs: false;

  /**
   * Locked to `true`. i18next normalizes casing, underscore-vs-hyphen, and
   * script-tag capitalization internally. Combined with our upstream
   * `resolveLocale` normalization, this is a defense-in-depth belt-and-
   * suspenders default that costs nothing. No legitimate reason to disable.
   */
  cleanCode: true;

  /**
   * `escapeValue` is locked to `false` at the LEAF level — universal across
   * every Joy service (audited across joy-web, email_service,
   * card/contact/form services, marcom, microfrontend). React and MJML both
   * escape at their render layer, so double-escaping here would render
   * `&amp;` and similar entities as literal text in the UI.
   *
   * OTHER interpolation fields (`format`, `formatSeparator`, `prefix`,
   * `suffix`, ...) from `overrides.interpolation` are preserved via a
   * leaf-level merge. Only `escapeValue` is forced.
   */
  interpolation: { escapeValue: false };

  /**
   * OPAQUE LOCKED BLOCK. `convertDetectedLanguage` is baked in to run
   * `resolveLocale` on every input before i18next sees it — that's the
   * whole point of centralized locale resolution. `lookupQuerystring` is
   * the one field that varies (joy-web uses `'l'` to avoid the hotel-
   * search `lng` longitude collision — JW-47), so it's exposed as an opt.
   *
   * Do NOT spread `detection: {...}` after this base config — you will
   * drop `convertDetectedLanguage` and defeat centralization. If you need
   * to extend detection (e.g. custom `order`), spread the base's detection
   * first:
   *
   *   const base = buildI18nextConfig({...});
   *   const config = { ...base, detection: { ...base.detection, order: [...] } };
   */
  detection: {
    convertDetectedLanguage: (lng: string) => string;
    lookupQuerystring?: string;
  };
}

/**
 * Build i18next init options that implement the joy-web PR #15872 behavior
 * as a portable, version-agnostic base config.
 *
 * @param opts.supportedLocales - The locales this service actually has
 *   translations for. Passed through as `supportedLngs`.
 * @param opts.fallbackLocale - The locale to use when no match is found.
 *   Passed through as `fallbackLng`.
 * @param opts.lookupQuerystring - Optional custom URL query-string key for
 *   language detection (joy-web uses `'l'` to avoid collision with the
 *   hotel-search `lng` longitude URL param — see JW-47). Omit to accept
 *   i18next's default.
 * @param opts.overrides - Additional i18next init options to spread into
 *   the returned config. Type parameterized so consumers can pass their
 *   own local `Partial<InitOptions>` type without this package coupling
 *   to a specific i18next version. NOTE: overrides are spread BEFORE the
 *   locked fields, so attempting to override a locked field is a no-op
 *   at runtime AND surfaces as `never` in the return type.
 *
 * @example
 * ```ts
 * import type { InitOptions } from 'i18next';
 * import { buildI18nextConfig } from '@withjoy/locale';
 *
 * i18next.init({
 *   ...buildI18nextConfig({
 *     supportedLocales: ['en-US', 'de-DE', 'fr-FR'],
 *     fallbackLocale: 'en-US',
 *     lookupQuerystring: 'l',
 *   }),
 *   // service-specific overrides (resources, ns, react, etc.)
 *   resources: myResources,
 * } satisfies InitOptions);
 * ```
 */
export function buildI18nextConfig<TOverrides extends object = object>(opts: {
  supportedLocales: readonly string[];
  fallbackLocale: string;
  lookupQuerystring?: string;
  overrides?: TOverrides;
}): I18nextBaseConfig & TOverrides {
  const { supportedLocales, fallbackLocale, lookupQuerystring, overrides } = opts;

  // Precompute the resolver ONCE. The returned closure reuses a bound
  // canonical → original lookup map across every convertDetectedLanguage
  // invocation — important for services with large supported-locale sets
  // where the middleware calls convertDetectedLanguage per HTTP request.
  const resolve = createResolver(supportedLocales, fallbackLocale);

  // Extract the caller's interpolation override (if any). We merge it into
  // the returned config at the LEAF field level (not by subtree replacement),
  // so consumers who pass e.g. `interpolation: { format: (v, fmt) => ... }`
  // for custom date/number formatting keep those fields on the returned
  // config while `escapeValue` stays locked to `false`.
  const overrideInterpolation = (overrides as { interpolation?: Record<string, unknown> } | undefined)
    ?.interpolation;

  return {
    ...(overrides ?? ({} as TOverrides)),
    // Canonicalize both fields so the codes emitted to i18next match what
    // convertDetectedLanguage returns for every detection event. Otherwise a
    // consumer passing `['en_us']` + `'en_us'` would end up with i18next's
    // supportedLngs and fallbackLng out of sync with the resolver's output
    // ('en-US'), and i18next would silently fail to find the target locale
    // in the resolved chain. Matches the same defensive canonicalization
    // done inside createResolver.
    supportedLngs: supportedLocales.map((l) => canonicalizeLocale(l) ?? l),
    fallbackLng: canonicalizeLocale(fallbackLocale) ?? fallbackLocale,
    load: 'all',
    nonExplicitSupportedLngs: false,
    cleanCode: true,
    // Leaf-level lock: preserve any consumer-supplied interpolation fields
    // (`format`, `formatSeparator`, ...) but always force `escapeValue: false`
    // because React and MJML both escape at their render layer — double-
    // escaping here renders `&amp;` as literal text in the UI.
    interpolation: { ...overrideInterpolation, escapeValue: false },
    // Detection block is documented as OPAQUE (see interface JSDoc). A
    // previous iteration tried `Object.freeze` to enforce that at runtime,
    // but i18next-http-middleware's LanguageDetector mutates the passed
    // detection object during init (fills in defaults for `order`,
    // `lookupCookie`, `caches`, etc. via a shared `utils.defaults` helper)
    // — freezing throws TypeError on that path. Keeping the guardrail
    // doc-only so we stay compatible with the i18next detector plugins.
    detection: {
      convertDetectedLanguage: (lng: string): string => resolve(lng).locale,
      lookupQuerystring,
    },
  } as I18nextBaseConfig & TOverrides;
}
